import wlLogo from '@images/logos/wl-logo.svg'
import PhosphorIcon from 'teacher-ui-client/components/phosphor-icon'
import fpLogo from '@images/logos/fp-logo.svg'
import rexLogo from '@images/logos/rex-logo.svg'
import rexLogoAu from '@images/logos/rex-logo-au.svg'
import msLogo from '@images/logos/ms-logo.svg'
import msLogoAu from '@images/logos/ms-logo-au.svg'
import reLogo from '@images/logos/re-logo.svg'
import reLogoAu from '@images/logos/re-logo-au.svg'
import essentialsLogo from '@images/logos/3e-logo.svg'
import essentialsLogoWhite from '@images/logos/3e-logo-white.svg'
import mathleticsCard from '@images/program/mathletics.svg'
import threePLearningLogoWhite from '@images/logos/3p-logo-white.svg'
import threePLearningLogo from '@images/logos/3p-logo.svg'
import handUnfilled from '@images/icons/hand-unfilled.svg'
import appleUnfilled from '@images/icons/apple-unfilled.svg'
import appSwitcher from '@images/icons/app-switcher.svg'
import threeEssentialsStar from '@images/3e_star.png'
import Component from '@glimmer/component'
import { service } from '@ember/service'
import { on } from '@ember/modifier'
import { SubscriptionType } from 'teacher-ui-client/subscription-type/subscription-types'
import type FeatureControlService from 'teacher-ui-client/services/feature-control'
import { canAccess } from 'teacher-ui-client/helpers/can-access'
import { LinkTo } from '@ember/routing'
import { array } from '@ember/helper'
import { eq } from 'ember-truth-helpers'
import { t, type IntlService } from 'ember-intl'
import type LogoutService from 'teacher-ui-client/services/logout'
import type PromoService from 'teacher-ui-client/services/promo'
import type { TOC } from '@ember/component/template-only'
import PopoverStandardDropdown from '@blakeelearning/popovers/components/popover/dropdowns/standard'
import config from 'teacher-ui-client/config/environment'
import { join } from 'teacher-ui-client/utils/uri'
import { AMER } from 'teacher-ui-client/feature-control/marketing-regions'
import type SideNavService from 'teacher-ui-client/services/side-nav'

interface Signature {
  Element: HTMLElement
}

interface TabItem {
  subType: SubscriptionType
  tabClassNames: string
  crossPromo?: boolean
}

const themes: Record<SubscriptionType, Record<PropertyKey, string>> = {
  [SubscriptionType.Reading]: {
    separator: 'border-purply-pink-350',
    navBar: 'bg-oceany-blue-300',
  },
  [SubscriptionType.Maths]: {
    separator: 'border-[#764b82]',
    navBar: 'bg-ms-green-300',
  },
  [SubscriptionType.Writing]: {
    separator: 'border-wl-blue-300',
    navBar: 'bg-glowy-orange-300 bg-writing-nav-bar',
  },
}

export class NavBar extends Component<Signature> {
  @service logout!: LogoutService

  @service sideNav!: SideNavService

  get appleUnfilledSvg() {
    return appleUnfilled
  }

  get handUnfilledSvg() {
    return handUnfilled
  }

  get selectedTheme() {
    if (!this.sideNav.currentSubscriptionType) {
      return themes[SubscriptionType.Reading]
    }
    return themes[this.sideNav.currentSubscriptionType]
  }

  get useAuLogos() {
    return this.sideNav.countryCode === 'AU'
  }

  get isAmerSchool() {
    return AMER.includes(this.sideNav.countryCode ?? '')
  }

  get headerImagePathReadingEggs() {
    const readingPath = this.useAuLogos ? reLogoAu : reLogo
    return readingPath
  }

  get headerImagePathMathseeds() {
    const mathsPath = this.useAuLogos ? msLogoAu : msLogo
    return mathsPath
  }

  get headerImagePathReadingEggspress() {
    const rexPath = this.useAuLogos ? rexLogoAu : rexLogo
    return rexPath
  }

  get headerImagePathFastPhonics() {
    const fpPath = fpLogo
    return fpPath
  }

  get headerImagePathWritingLegends() {
    return wlLogo
  }

  logoutAndRedirectToPuiSupport = () => {
    void this.logout.logoutAndRedirectToReadingeggs('/parent_ui/contact-us')
  }

  logoutAndRedirectToPuiDashboard = () => {
    void this.logout.logoutAndRedirectToReadingeggs('/parent_ui/redirect/home')
  }

  <template>
    <header class="relative" ...attributes>
      <nav class={{this.selectedTheme.navBar}}>
        <div class="flex justify-between {{if (canAccess 'one_roster') 'mr-2 ml-2 lg:mr-8' 'mx-2 lg:mx-8'}}">
          <div class="flex grow gap-4">
            {{#if (canAccess "one_roster")}}
              <div class="my-2 flex shrink-0 items-center gap-2">
                <AppSwitcher class="shrink-0" @isAmerSchool={{this.isAmerSchool}} />
                <img
                  data-test-navbar-logo={{if this.isAmerSchool "3p" "3e"}}
                  src={{if this.isAmerSchool threePLearningLogoWhite essentialsLogoWhite}}
                  alt={{t "3-essentials"}}
                  class="h-10"
                />
              </div>
            {{/if}}

            <SubscriptionTypeTabs />

          </div>

          <div class="hidden h-16 grow justify-center gap-3 py-2 drop-shadow-[2px_1px_2px_rgba(0,0,0,0.4)] xl:flex">
            {{#if (eq "reading" this.sideNav.currentSubscriptionType)}}
              <img
                src={{this.headerImagePathReadingEggs}}
                class="{{unless this.useAuLogos 'py-2'}}"
                alt={{t "products.re"}}
              />
              <img src={{this.headerImagePathReadingEggspress}} alt={{t "products.rex"}} />
              <img src={{this.headerImagePathFastPhonics}} alt={{t "products.fp"}} />
            {{else if (eq "maths" this.sideNav.currentSubscriptionType)}}
              <img src={{this.headerImagePathMathseeds}} alt={{t "products.ms"}} />
            {{else if (eq "writing" this.sideNav.currentSubscriptionType)}}
              <img src={{this.headerImagePathWritingLegends}} alt={{t "products.writing-legends"}} />
            {{/if}}
          </div>
          {{! Below medium screensize, these links are removed from the navbar and are located in the sidenav instead }}
          <ul class="hidden grow items-center justify-end gap-1 whitespace-nowrap md:flex lg:gap-8">
            {{#if this.sideNav.isHomeschool}}
              <li class="font-semibold drop-shadow-[0px_2px_4px_rgb(0,0,0,0.2)]">
                <button
                  {{on "click" this.logoutAndRedirectToPuiSupport}}
                  data-test-navbar-homeschool-contact-us
                  class="text-white"
                  type="button"
                >
                  {{t "navBar.support"}}
                </button>
              </li>
            {{else}}
              <li class="font-semibold drop-shadow-[0px_2px_4px_rgb(0,0,0,0.2)]">
                <button data-test-navbar-walk-me-button id="walkme-support" class="text-white" type="button">
                  {{t "navBar.support"}}
                </button>
              </li>
            {{/if}}

            {{#unless this.sideNav.limitedAccess}}
              <li class="font-semibold drop-shadow-[0px_2px_4px_rgb(0,0,0,0.2)]">

                <LinkTo
                  @route="subscription-type.account"
                  class="text-white hover:text-white focus:text-white active:text-white"
                  data-test-navbar-my-account
                >
                  <img alt={{t "navBar.apple"}} class="inline-block" src={{this.appleUnfilledSvg}} />
                  {{t "navBar.myAccount"}}
                </LinkTo>

              </li>
            {{/unless}}
            <li class="font-semibold drop-shadow-[0px_2px_4px_rgb(0,0,0,0.2)]">
              <button
                data-test-navbar-logout
                type="button"
                class="text-white"
                {{on
                  "click"
                  (if this.sideNav.isHomeschool this.logoutAndRedirectToPuiDashboard this.logout.logoutAndRedirect)
                }}
              >
                {{#if this.sideNav.isHomeschool}}
                  <PhosphorIcon @icon="phosphor-users-three-bold" class="mr-2 inline-block" />
                  <span>{{t "navBar.returnToFamilyDashboard"}}</span>
                {{else}}
                  <img alt={{t "navBar.wavingHand"}} class="inline-block" src={{this.handUnfilledSvg}} />
                  {{t "navBar.logout"}}
                {{/if}}
              </button>
            </li>
          </ul>
        </div>
        <div class="border-b-8 {{this.selectedTheme.separator}}"></div>
      </nav>
    </header>
  </template>
}

class SubscriptionTypeTabs extends Component {
  @service featureControl!: FeatureControlService

  @service promo!: PromoService

  get tabs(): TabItem[] {
    const tabs = [
      {
        subType: SubscriptionType.Reading,
        tabClassNames:
          // changes the background gradient colour "from" & "to" used on hover and focus. does not change if active
          'border-purply-pink-200 bg-linear-to-b from-[#e351a1] to-purply-pink-350 active-link:from-[#e351a1] active-link:to-purply-pink-350 hover:from-[#ce438f] hover:to-[#952461] focus:from-[#ce438f] focus:to-[#952461]',
        crossPromo: this.promo.showReRosterCrossPromo,
      },
      {
        subType: SubscriptionType.Maths,
        tabClassNames:
          'border-[#e9a4fc] bg-linear-to-b from-[#c57dda] to-[#764b82] active-link:from-[#c57dda] active-link:to-[#764b82] hover:from-[#ad6ebf] hover:to-[#55355e] focus:from-[#ad6ebf] focus:to-[#55355e]',
        crossPromo: this.promo.showMsRosterCrossPromo,
      },
    ]

    if (this.featureControl.canAccess('writing_area')) {
      const writingTab = {
        subType: SubscriptionType.Writing,
        tabClassNames:
          'border-wl-blue-150 bg-linear-to-b from-wl-blue-250 to-wl-blue-300 active-link:from-wl-blue-250 active-link:to-wl-blue-300 hover:from-wl-blue-250 hover:to-wl-blue-400 focus:from-wl-blue-250 focus:to-wl-blue-400',
        crossPromo: this.promo.showWlRosterCrossPromo,
      }
      return [...tabs, writingTab]
    }

    return tabs
  }

  <template>
    <div class="flex items-center gap-2 self-end pt-4">

      {{#each this.tabs as |tab|}}
        {{#if tab.crossPromo}}
          <CrossPromoNavTab @tab={{tab}} />
        {{else}}
          <NavTab @tab={{tab}} />
        {{/if}}
      {{/each}}
    </div>
  </template>
}

interface NavTabSignature {
  Args: {
    tab: TabItem
  }
}

class NavTab extends Component<NavTabSignature> {
  @service promo!: PromoService

  @service intl!: IntlService

  get showNewIndicator() {
    const mathsUpsell = this.args.tab.subType === SubscriptionType.Maths && this.promo.shouldShowMSUpsell
    const mathsCrossSell = this.args.tab.subType === SubscriptionType.Maths && this.promo.shouldShowMsCrossSell
    const reCrossSell = this.args.tab.subType === SubscriptionType.Reading && this.promo.shouldShowReCrossSell
    return mathsUpsell || mathsCrossSell || reCrossSell
  }

  get tabText(): string {
    return {
      [SubscriptionType.Reading]: this.intl.t('reading'),
      [SubscriptionType.Maths]: this.intl.t('maths'),
      [SubscriptionType.Writing]: this.intl.t('writing'),
    }[this.args.tab.subType]
  }

  <template>
    <LinkTo
      data-test-navbar-tab={{@tab.subType}}
      class="active-link:shadow-[0_-2px_2px_rgba(0,0,0,0.2)] relative inline-block rounded-t-md border-x border-t px-4 py-3 text-white shadow-[0_-2px_2px_rgba(0,0,0,0.1)] hover:text-white focus:text-white active:text-white
        {{@tab.tabClassNames}}"
      @route="subscription-type.home"
      @current-when="subscription-type"
      @models={{array @tab.subType}}
    >
      {{#if this.showNewIndicator}}<NewIndicator>{{t "promo.new"}}</NewIndicator>{{/if}}
      {{this.tabText}}
    </LinkTo>
  </template>
}

class CrossPromoNavTab extends Component<NavTabSignature> {
  @service intl!: IntlService

  get tabText(): string {
    return {
      [SubscriptionType.Reading]: this.intl.t('crossPromo.reading'),
      [SubscriptionType.Maths]: this.intl.t('crossPromo.maths'),
      [SubscriptionType.Writing]: this.intl.t('crossPromo.writing'),
    }[this.args.tab.subType]
  }

  get returnTo(): string {
    return {
      [SubscriptionType.Reading]: '/reading-eggs',
      [SubscriptionType.Maths]: '/mathseeds',
      [SubscriptionType.Writing]: '/writing-legends',
    }[this.args.tab.subType]
  }

  get tabHref(): string {
    const baseUrl = join(config.oneRosterUrl, 'login')
    const crossPromoUrl = new URL(baseUrl)
    crossPromoUrl.searchParams.set('return_to', this.returnTo)

    return crossPromoUrl.toString()
  }

  <template>
    <a
      data-test-navbar-tab={{@tab.subType}}
      class="active-link:shadow-[0_-2px_2px_rgba(0,0,0,0.2)] relative flex items-center gap-1 rounded-t-md border-x border-t px-4 py-3 text-white shadow-[0_-2px_2px_rgba(0,0,0,0.1)] hover:text-white focus:text-white active:text-white
        {{@tab.tabClassNames}}"
      href={{this.tabHref}}
      target="_blank"
      rel="noopener noreferrer"
    >

      {{this.tabText}}
      <PhosphorIcon @icon="phosphor-arrow-square-out-bold" class="h-4 w-4" />

      <img
        data-test-navbar-cross-promo-indicator
        class="absolute top-[-0.3rem] -right-2 h-5 w-5 rotate-12"
        alt=""
        src={{threeEssentialsStar}}
      />

    </a>
  </template>
}

const NewIndicator: TOC<{ Blocks: { default: [] } }> = <template>
  <div
    data-test-navbar-new-indicator
    class="animate-wiggle bg-juicy-orange-400 absolute top-[-0.6rem] -right-2 rotate-12 rounded-sm border border-white px-1 text-sm uppercase"
  >{{yield}}</div>
</template>

interface AppSwitcherSignature {
  Element: HTMLDivElement
  Args: {
    isAmerSchool?: boolean
  }
}

const AppSwitcher: TOC<AppSwitcherSignature> = <template>
  <div data-test-navbar-app-switcher ...attributes>
    <PopoverStandardDropdown as |dropdown status|>
      <button
        data-test-dropdown-trigger
        type="button"
        {{dropdown.makeTrigger}}
        class="hover:bg-oceany-blue-325 selected:bg-oceany-blue-350 grid cursor-pointer place-content-center rounded-lg p-2
          {{if status.isOpen 'bg-oceany-blue-350'}}"
      >
        <img class="h-6 w-6 shadow-xs" src={{appSwitcher}} alt={{t "3-essentials.icons.app-switcher"}} />
      </button>
      <dropdown.content>
        <div data-test-dropdown-content class="border-neutral-75 max-w-xs rounded-lg border bg-white p-4 lg:max-w-md">
          <div class="mb-2 text-xl font-medium">{{t "3-essentials.app-switcher.switch"}}</div>
          {{! Content top border, should line up with the controls border }}
          <div class="space-y-4">
            <ProgramCard3E @isAmerSchool={{@isAmerSchool}} />
            <ProgramCardMathletics @hasSubType={{true}} />
          </div>
        </div>
      </dropdown.content>
    </PopoverStandardDropdown>
  </div>
</template>

interface ProgramCardSignature {
  Element: HTMLAnchorElement
  Args: {
    hasSubType?: boolean
    isAmerSchool?: boolean
  }
}

const oneRosterUrl = join(config.oneRosterUrl, 'login').toString()

const ProgramCard3E: TOC<ProgramCardSignature> = <template>
  <div class="from-3E-pink-300 via-3E-blue-300 to-3E-green-300 rounded-lg bg-linear-to-r p-0.5">
    <a
      data-test-navbar-3e-card
      href={{oneRosterUrl}}
      class="hover:from-3E-pink-50 hover:via-3E-blue-50 hover:to-3E-green-50 active:from-3E-pink-150 active:via-3E-blue-150 active:to-3E-green-150 block space-y-2 rounded-md bg-white px-4 py-3 hover:bg-linear-to-r active:bg-linear-to-r"
      ...attributes
    >
      <div class="flex max-h-10 gap-4">
        <img src={{if @isAmerSchool threePLearningLogo essentialsLogo}} alt={{t "3-essentials.icons.essentials"}} />
      </div>
      <div class="space-y-1">
        <div class="text-xl font-medium text-black">{{t "3-essentials.app-switcher.essentials.heading"}}</div>
        <div class="text-black">{{t "3-essentials.app-switcher.essentials.description"}}</div>
      </div>
    </a>
  </div>
</template>

class ProgramCardMathletics extends Component<ProgramCardSignature> {
  @service promo!: PromoService

  get crossPromoUrl() {
    if (this.promo.showMxRosterCrossPromo) {
      const baseUrl = join(config.oneRosterUrl, 'login')
      const crossPromoUrl = new URL(baseUrl)
      crossPromoUrl.searchParams.set('return_to', '/mathletics')

      return crossPromoUrl.toString()
    }
  }

  <template>
    <a
      data-test-navbar-3e-mathletics-card
      href={{if this.crossPromoUrl this.crossPromoUrl config.mathleticsUrl}}
      class="block space-y-2 rounded-lg border-2 px-4 py-3
        {{if
          @hasSubType
          'border-3E-blue-300 hover:bg-3E-blue-50'
          'border-3E-blue-300/20 cursor-default bg-neutral-50'
        }}"
      target={{if this.crossPromoUrl "_blank"}}
      rel={{if this.crossPromoUrl "noopener noreferrer"}}
      ...attributes
    >
      <div class="flex max-h-10 gap-4 {{unless @hasSubType 'opacity-40'}}">
        <img src={{mathleticsCard}} alt={{t "3-essentials.icons.mathletics"}} />
      </div>
      <div class="space-y-1">
        <ProgramCardBody
          @hasSubType={{@hasSubType}}
          @heading={{t "3-essentials.app-switcher.mathletics.heading"}}
          @description={{t "3-essentials.app-switcher.mathletics.description"}}
        />
      </div>
    </a>
  </template>
}

interface ProgramCardBodySignature {
  Args: {
    hasSubType?: boolean
    heading: string
    description: string
  }
}

const ProgramCardBody: TOC<ProgramCardBodySignature> = <template>
  <div class="text-xl font-medium {{if @hasSubType 'text-black' 'text-neutral-300'}} text-black">
    {{#unless @hasSubType}}
      <PhosphorIcon data-test-navbar-3e-warning @icon="phosphor-warning-fill" class="text-neutral-200" />
    {{/unless}}
    {{@heading}}
  </div>
  <div data-class-3e-card-body class="{{if @hasSubType 'text-black' 'text-neutral-300'}}">
    {{#if @hasSubType}}
      {{@description}}
    {{else}}
      {{t "3-essentials.app-switcher.invalid.body"}}
      <LinkTo @route="subscription-type.home">{{t "3-essentials.app-switcher.invalid.link"}}</LinkTo>
    {{/if}}
  </div>
</template>

export default NavBar
